/**
 * Stable 16-hex-char id for a provider item: the first 16 characters of
 * SHA-1(`${sourceId}:${providerId}`).
 *
 * These ids key favourites, watch history, parental locks and guide mappings,
 * so they must be byte-identical wherever the catalog is built — the server
 * today, and the APK's WebView once it talks to the provider directly (Section
 * 22 Phase 1). That is why this does not use `node:crypto`: it would not exist
 * in a WebView, and the browser's `crypto.subtle.digest` is async, which would
 * ripple through every synchronous catalog normalizer that calls this. The
 * test suite pins the output to node:crypto's across ASCII, multi-byte UTF-8
 * and every padding boundary, so switching implementations cannot silently
 * re-key anyone's data.
 */
export function hashId(sourceId, providerId) {
    return sha1Hex(`${sourceId}:${providerId}`).slice(0, 16);
}
export function slugify(input) {
    const slug = input
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return slug || "uncategorized";
}
// Reused across calls: a full catalog hashes ~60,000 ids, and allocating these
// per call roughly doubled the cost. Safe because sha1Hex is synchronous and
// never re-entered.
const encoder = new TextEncoder();
const schedule = new Array(80).fill(0);
/** SHA-1 (FIPS 180-4) of the UTF-8 encoding of `input`, lowercase hex. */
export function sha1Hex(input) {
    const bytes = encoder.encode(input);
    // Pad to a multiple of 64 bytes: 0x80, zeros, then the 64-bit bit-length.
    const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
    const padded = new Uint8Array(paddedLength);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    const bitLength = bytes.length * 8;
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000));
    view.setUint32(paddedLength - 4, bitLength >>> 0);
    let h0 = 0x67452301;
    let h1 = 0xefcdab89;
    let h2 = 0x98badcfe;
    let h3 = 0x10325476;
    let h4 = 0xc3d2e1f0;
    const w = schedule;
    for (let block = 0; block < paddedLength; block += 64) {
        for (let i = 0; i < 16; i += 1)
            w[i] = view.getUint32(block + i * 4);
        for (let i = 16; i < 80; i += 1) {
            const x = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
            w[i] = ((x << 1) | (x >>> 31)) >>> 0;
        }
        let a = h0;
        let b = h1;
        let c = h2;
        let d = h3;
        let e = h4;
        for (let i = 0; i < 80; i += 1) {
            let f;
            let k;
            if (i < 20) {
                f = (b & c) | (~b & d);
                k = 0x5a827999;
            }
            else if (i < 40) {
                f = b ^ c ^ d;
                k = 0x6ed9eba1;
            }
            else if (i < 60) {
                f = (b & c) | (b & d) | (c & d);
                k = 0x8f1bbcdc;
            }
            else {
                f = b ^ c ^ d;
                k = 0xca62c1d6;
            }
            const temp = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) >>> 0;
            e = d;
            d = c;
            c = ((b << 30) | (b >>> 2)) >>> 0;
            b = a;
            a = temp;
        }
        h0 = (h0 + a) >>> 0;
        h1 = (h1 + b) >>> 0;
        h2 = (h2 + c) >>> 0;
        h3 = (h3 + d) >>> 0;
        h4 = (h4 + e) >>> 0;
    }
    return [h0, h1, h2, h3, h4].map((word) => word.toString(16).padStart(8, "0")).join("");
}
//# sourceMappingURL=hash.js.map